import { useMutation, useQuery } from '@apollo/client'

import { gql } from '@/clients/utoniq-core'
import {
  ApplyMerchItemDeliveryMutation,
  ApplyMerchItemDeliveryMutationVariables,
  DeliveryItemBoxFragmentDoc,
  DeliveryItemsConnectionFragmentDoc,
  DeliveryManagementQuery,
  DeliveryManagementQueryVariables,
  DeliveryStatus,
  SendVerificationSmsMutation,
  SendVerificationSmsMutationVariables,
  UserAddressFragmentDoc,
} from '@/clients/utoniq-core/schema'
import { config } from '@/config'
import { useMessage, useRouter, useTranslation } from '@/core'
import { makeViewModel } from '@/core/architecture'

import { Action, State } from './template'

/**
 * @note
 * 配送アイテムの初期表示タブ
 * 仕組み上、未申請ステータスを設けないアプリは「申請済み」を初期表示とする
 */
const initialTab = ['machizero'].includes(config.applicationKey)
  ? [DeliveryStatus.Arranging, DeliveryStatus.Shipped]
  : [DeliveryStatus.Unapplied]

export const useViewModel = makeViewModel<State, Action>(() => {
  const { showError, showMessage } = useMessage()
  const { router, routerPages } = useRouter()
  const { t } = useTranslation('deliveryManagement')

  const { data, refetch, loading, fetchMore } = useQuery<
    DeliveryManagementQuery,
    DeliveryManagementQueryVariables
  >(QUERY, {
    variables: { deliveryItemsStatusFilter: initialTab },
    returnPartialData: true,
  })
  const [applyMerchItemDelivery, { loading: merchItemDeliveryApplying }] =
    useMutation<
      ApplyMerchItemDeliveryMutation,
      ApplyMerchItemDeliveryMutationVariables
    >(APPLY_MERCH_ITEM_DELIVERY)

  const [sendVerificationSMS, { loading: sendingVerification }] = useMutation<
    SendVerificationSmsMutation,
    SendVerificationSmsMutationVariables
  >(SEND_VERIFICATION_SMS)

  return {
    state: {
      initialTab,
      loading,
      merchItemDeliveryApplying,
      hasNextPage: !!data?.deliveryItems?.pageInfo?.hasNextPage,
      deliveryItems: data?.deliveryItems ?? null,
      user: data?.user ?? null,
      sendingVerification: sendingVerification,
    },
    actions: {
      async onChangeStatus(status) {
        await refetch({ deliveryItemsStatusFilter: status })
      },
      async onFetchMore(status) {
        await fetchMore({
          variables: {
            deliveryItemsStatusFilter: status,
            pageInput: { cursor: data?.deliveryItems?.pageInfo.endCursor },
          },
        })
      },
      async onApply({ id }) {
        await applyMerchItemDelivery({
          variables: { applyMerchItemDeliveryIds: [id] },
        })
          .then(() => showMessage(t('applySuccess'), 'success'))
          .catch(showError)
      },
      async onSendVerificationSMS() {
        sendVerificationSMS()
          .then(() => {
            router.push(routerPages.SMSVerification)
          })
          .catch(e => {
            showError(e)
          })
      },
    },
  }
})

const QUERY = gql`
  query DeliveryManagement(
    $pageInput: PageInput
    $deliveryItemsStatusFilter: [DeliveryStatus!]
  ) {
    user {
      id
      phoneNumber
      formattedPhoneNumber @client
      isPhoneNumberVerified
      ...UserAddress
    }
    deliveryItems(
      pageInput: $pageInput
      statusFilter: $deliveryItemsStatusFilter
    ) {
      ...DeliveryItemsConnection
    }
  }
  ${UserAddressFragmentDoc}
  ${DeliveryItemsConnectionFragmentDoc}
`

const APPLY_MERCH_ITEM_DELIVERY = gql`
  mutation ApplyMerchItemDelivery($applyMerchItemDeliveryIds: [ID!]!) {
    applyMerchItemDelivery(ids: $applyMerchItemDeliveryIds) {
      ...DeliveryItemBox
    }
  }
  ${DeliveryItemBoxFragmentDoc}
`

const SEND_VERIFICATION_SMS = gql`
  mutation SendVerificationSMS {
    sendVerificationSMS {
      phoneNumber
      id
    }
  }
`
